// MARK: NPM Modules
import React, { useState, useEffect } from "react";
import { graphql } from "gatsby";
// MARK: Redux
import store from "reduxFunctionality/index";
import { Provider } from "react-redux";
// MARK: Types
import Props from "types/pages/blog/post/props";
// import HowToStructuredDataSet from "types/seo/howToStructuredDataSet";
// MARK: Components
import Layout from "layouts/blog/post";
import Styleguide from "globals/styleguide";
import NavigationBar from "globals/navigationBar";
import Footer from "globals/footer";
// MARK: Functionality
import MetaData from "utils/seo/metaData";
import GlobalStyles from "utils/styleguide/documentBase";
import ScrollToAnchor from "utils/common/scrollToHash";
import InitializeGoogleAnalytics from "utils/google-analytics";
import getSEOFromData from "utils/seo/seoFromData";
import getHowToStructuredDataSetFromData from "utils/seo/howToStructuredDatasSetFromData";
// MARK: Fragments
// @ts-ignore
import {
  StructuredDataFAQQuestionAndAnswer,
  StructuredDataHowToStep,
  StructuredDataHowToStepWithDirections,
  MultilineHeadlineFragment,
  AuthorFragment,
  VideoFragment,
  ImageFragment,
  YoutubeFragment,
  VimeoFragment,
} from "src/graphql";

// MARK: React Component
const BlogPostPage = ({ pageContext, data }: Props) => {
  // MARK: State Variables
  const [styleguideAvailable, setStyleguideAvailable] =
    useState<boolean>(false);
  // MARK: Use Effects
  useEffect(() => {
    InitializeGoogleAnalytics();
  }, []);
  useEffect(() => {
    ScrollToAnchor();
  }, [styleguideAvailable]);
  // MARK: Variables
  const { styleguide, navigationBar, footer } = pageContext;
  // Functionality
  const renderScene = () => {
    var scene: any = [];
    scene.push(<NavigationBar key="navigation-bar" data={navigationBar} />);
    scene.push(
      <Layout
        key="layout"
        layout={data.craftApiBlogPostEntry}
        seo={getSEOFromData(data.craftApiBlogPostEntry.uri, data.craftApiBlogPostEntry)}
      />
    );
    scene.push(
      <Footer key="footer" data={footer} navigationBarData={navigationBar} />
    );
    return scene;
  };

  // MARK: Render
  return (
    <Provider store={store}>
      <GlobalStyles styleguide={styleguide} />
      <MetaData
        seo={getSEOFromData(data.craftApiBlogPostEntry.uri, data.craftApiBlogPostEntry)}
        structuredDataFAQ={data.craftApiBlogPostEntry.structuredDataFAQ}
        howToStructuredDataSet={getHowToStructuredDataSetFromData(
          data.craftApiBlogPostEntry
        )}
        isArticle={true}
      />
      <Styleguide
        styleguide={styleguide}
        onStyleguideUpdate={() => {
          if (!styleguideAvailable) {
            setStyleguideAvailable(true);
          }
        }}
      />
      {renderScene()}
    </Provider>
  );
};

export default BlogPostPage;

export const query = graphql`
  query IndividualBlogPostEntryQuery(
    $id: ID
    $includeStructuredFAQ: Boolean!
    $includeStructuredHowTo: Boolean!
    $includeStructuredHowToStep: Boolean!
    $includeStructuredHowToStepWithDirections: Boolean!
    $heroIsAnImage: Boolean!
    $includeSectionTitle: Boolean!
    $includeSectionSubTitle: Boolean!
    $includeSectionSubSubTitle: Boolean!
    $includeCopy: Boolean!
    $includeButton: Boolean!
    $includeYoutube: Boolean!
    $includeVimeo: Boolean!
    $includeQuote: Boolean!
    $includeGist: Boolean!
    $includeMarkdown: Boolean!
  ) {
    craftApiBlogPostEntry(remoteId: { eq: $id }) {
      uri
      dateCreated
      dateUpdated

      metaTitle
      metaDescription
      author {
        ...AuthorFragment
      }
      seoImage {
        ... on CraftAPI_seo_seoImage_Entry {
          seoImageAsset {
            url
            width
            height
          }
        }
      }

      structuredDataFAQ @include(if: $includeStructuredFAQ) {
        ...StructuredDataFAQQuestionAndAnswer
      }

      integrateStructuredDataHowTo @include(if: $includeStructuredHowTo)
      structuredDataHowToQuestion @include(if: $includeStructuredHowTo)
      structuredDataHowToLeadImage @include(if: $includeStructuredHowTo) {
        ...ImageFragment
      }
      structuredDataHowTo @include(if: $includeStructuredHowTo) {
        ...StructuredDataHowToStep @include(if: $includeStructuredHowToStep)
        ...StructuredDataHowToStepWithDirections
          @include(if: $includeStructuredHowToStepWithDirections)
      }

      title
      multilineHeadline {
        ...MultilineHeadlineFragment
      }
      description
      hero {
        ...ImageFragment @include(if: $heroIsAnImage)
        ...VideoFragment @skip(if: $heroIsAnImage)
      }
      blogPostType
      blogHeroSubtitle
      blogContentBlocks {
        ... on CraftAPI_blogContentBlocks_sectionTitle_BlockType
          @include(if: $includeSectionTitle) {
          typeHandle
          copy
        }
        ... on CraftAPI_blogContentBlocks_sectionSubTitle_BlockType
          @include(if: $includeSectionSubTitle) {
          typeHandle
          copy
        }
        ... on CraftAPI_blogContentBlocks_quote_BlockType
          @include(if: $includeQuote) {
          typeHandle
          copy
          source
        }
        ... on CraftAPI_blogContentBlocks_copy_BlockType
          @include(if: $includeCopy) {
          typeHandle
          copy
        }
        ... on CraftAPI_blogContentBlocks_sectionSubSubTitle_BlockType
          @include(if: $includeSectionSubSubTitle) {
          typeHandle
          copy
        }
        ... on CraftAPI_blogContentBlocks_button_BlockType
          @include(if: $includeButton) {
          typeHandle
          marginTop
          buttonTitle
          page {
            sectionHandle
            uri
            ... on CraftAPI_externalLinks_mvp_Entry {
              externalUrl
            }
          }
          buttonType
        }
        ... on CraftAPI_blogContentBlocks_media_BlockType {
          typeHandle
          media {
            ...ImageFragment
            ...YoutubeFragment @include(if: $includeYoutube)
            ...VimeoFragment @include(if: $includeVimeo)
          }
          subtitle
        }
        ... on CraftAPI_blogContentBlocks_markdown_BlockType
          @include(if: $includeMarkdown) {
          typeHandle
          code
        }
        ... on CraftAPI_blogContentBlocks_gist_BlockType
          @include(if: $includeGist) {
          typeHandle
          reference
        }
      }
    }
  }
`;
